import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useState } from 'react'
import type { ApiError } from '@/lib/api'
import { institutionResetPassword } from '@/lib/institution-password'
import { parsePasswordResetQueryParams } from '@/lib/password-reset-url'
import loginStyles from '@/pages/LoginPage/LoginPage.module.css'

const INVALID_LINK_MSG =
  'Link inválido ou expirado. Solicite uma nova recuperação de senha.'

function extractErrorMessage(error: unknown): string {
  const fallback = INVALID_LINK_MSG
  if (!error || typeof error !== 'object') return fallback
  const apiError = error as ApiError
  const details = apiError.details as
    | { message?: string; errors?: Record<string, string[]> }
    | undefined

  if (details?.errors) {
    const firstKey = Object.keys(details.errors)[0]
    const firstMessage = firstKey ? details.errors[firstKey]?.[0] : undefined
    if (firstMessage) return firstMessage
  }

  if (details?.message) return details.message
  return apiError.message || fallback
}

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { token, email } = parsePasswordResetQueryParams(searchParams)
  const hasValidParams = Boolean(token && email)

  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!hasValidParams) return

    if (password.length < 8) {
      setErrorMessage('A senha deve ter pelo menos 8 caracteres.')
      return
    }

    if (password !== passwordConfirmation) {
      setErrorMessage('As senhas não conferem.')
      return
    }

    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      await institutionResetPassword({
        email: email.trim().toLowerCase(),
        token,
        password,
        password_confirmation: passwordConfirmation,
      })
      navigate('/login', { replace: true })
    } catch (error) {
      setErrorMessage(extractErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!hasValidParams) {
    return (
      <div className={loginStyles.page}>
        <div className={loginStyles.card}>
          <h1 className={loginStyles.title}>Link inválido</h1>
          <p className={loginStyles.subtitle}>{INVALID_LINK_MSG}</p>
          <div className={loginStyles.footer}>
            <p className={loginStyles.footerText}>
              <Link to="/recuperar-senha" className={loginStyles.signupLink}>
                Solicitar novo link
              </Link>
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={loginStyles.page}>
      <div className={loginStyles.card}>
        <h1 className={loginStyles.title}>Nova senha</h1>
        <p className={loginStyles.subtitle}>Digite e confirme sua nova senha</p>

        <form className={loginStyles.form} onSubmit={handleSubmit}>
          <div className={loginStyles.field}>
            <label className={loginStyles.label} htmlFor="reset-password">
              Nova senha
            </label>
            <input
              id="reset-password"
              type="password"
              className={loginStyles.input}
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>

          <div className={loginStyles.field}>
            <label className={loginStyles.label} htmlFor="reset-password-confirm">
              Confirmar senha
            </label>
            <input
              id="reset-password-confirm"
              type="password"
              className={loginStyles.input}
              placeholder="Repita a senha"
              autoComplete="new-password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              minLength={8}
            />
          </div>

          {errorMessage && (
            <div className={loginStyles.field} role="alert">
              <span className={loginStyles.subtitle}>{errorMessage}</span>
            </div>
          )}

          <button type="submit" className={loginStyles.submit} disabled={isSubmitting}>
            <Icon icon="mdi:lock-reset" className={loginStyles.submitIcon} />
            {isSubmitting ? 'Salvando...' : 'Redefinir senha'}
          </button>
        </form>

        <div className={loginStyles.footer}>
          <p className={loginStyles.footerText}>
            Lembrou a senha?
            <Link to="/login" className={loginStyles.signupLink}>
              Voltar ao login
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
